// Formatting shared by the inbox. User-visible text only; no dashes.
import { useEffect, useState } from 'react'
import type { FailureReason, Message } from '../api/inbox'

const time = new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' })
const day = new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'short' })
const full = new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' })

export function shortTime(iso: string, now: number): string {
  const d = new Date(iso)
  return now - d.getTime() < 24 * 3600_000 && new Date(now).getDate() === d.getDate() ? time.format(d) : day.format(d)
}

export function fullTime(iso: string): string {
  return full.format(new Date(iso))
}

// useNow re-renders every interval so countdowns stay true without a refetch.
export function useNow(everyMs = 30_000): number {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), everyMs)
    return () => clearInterval(t)
  }, [everyMs])
  return now
}

// "Window" is Meta jargon, so the labels say what it means for the team:
// a typed reply can be sent for 24 hours after the customer last wrote.
// Not "free": service messages past 1,000 a month per number are billed.
export type WindowState =
  | { open: true; label: string; short: string; endingSoon: boolean }
  | { open: false; label: string; short: string; reason: string }

export function windowState(expiresAt: string | undefined, now: number): WindowState {
  if (!expiresAt) {
    return {
      open: false,
      label: 'Waiting for the customer',
      short: 'Waiting for customer',
      reason: 'This customer has not messaged you yet, so a typed reply cannot be sent. They need to message you first.',
    }
  }
  const left = new Date(expiresAt).getTime() - now
  if (left <= 0) {
    return {
      open: false,
      label: 'Reply time ended',
      short: 'Reply time ended',
      reason: `Reply time ended on ${fullTime(expiresAt)}, 24 hours after the customer last wrote. You can type a reply again once they message you.`,
    }
  }
  const h = Math.floor(left / 3600_000)
  const m = Math.floor((left % 3600_000) / 60_000)
  const leftText = h > 0 ? `${h}h ${m}m` : `${m}m`
  const endingSoon = left < 2 * 3600_000
  return {
    open: true,
    label: endingSoon ? `Reply time ends in ${leftText}` : `Can reply, ${leftText} left`,
    short: endingSoon ? `Ends in ${leftText}` : `${h > 0 ? `${h}h` : `${m}m`} left to reply`,
    endingSoon,
  }
}

export const failureReasonText: Record<FailureReason, string> = {
  window_closed: 'reply time had ended, 24 hours after the customer last wrote',
  user_cap: 'Meta limits how many marketing messages this customer gets',
  undeliverable: 'the number is not on WhatsApp or has blocked you',
  unsupported: 'WhatsApp does not support this message type here',
  template: 'the template was rejected',
  payment_required: 'the WhatsApp account has no valid payment method at Meta',
  number_disconnected: 'the WhatsApp number is disconnected',
  token_invalid: 'the WhatsApp account needs to be reconnected',
  rejected: 'Meta rejected it',
  retries_exhausted: 'Meta was unavailable after several tries',
  outcome_unknown: 'we could not confirm whether Meta accepted it, so it was not resent',
}

export function deliveryLabel(m: Message): string {
  switch (m.status) {
    case 'pending':
      // Accepted means Meta took it; the delivery report comes later.
      return m.accepted ? 'Accepted by WhatsApp' : 'Sending'
    case 'sent':
      return 'Sent'
    case 'delivered':
      return 'Delivered'
    case 'read':
      return 'Read'
    case 'failed':
      return `Not delivered: ${m.failureReason ? failureReasonText[m.failureReason] : (m.errorTitle ?? 'Meta reported a failure')}`
    default:
      return ''
  }
}

// What to show for a message body we do not render yet.
export function bodyText(m: { type: string; body: string }): string {
  // A template's body is the text the customer read; the timeline labels it.
  if (m.type === 'text' || m.type === 'template') return m.body
  const kind = m.type.charAt(0).toUpperCase() + m.type.slice(1)
  return m.body ? `${kind}: ${m.body}` : `${kind} (preview not supported yet)`
}

export function newId(): string {
  return crypto.randomUUID()
}

// Up to two letters for an avatar. Words without a letter, like "(local", are
// skipped, and a Tamil or Devanagari letter stays whole with its vowel sign.
export function initials(name: string): string {
  return name
    .split(/\s+/)
    .map((w) => w.match(/^\p{L}\p{M}*/u)?.[0] ?? '')
    .filter(Boolean)
    .slice(0, 2)
    .map((l) => l.toUpperCase())
    .join('')
}
